/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import PropTypes from 'prop-types'
import React from 'react'

import {
    Box,
    Flex,
    Skeleton,
    Text,
    useMediaQuery,
    useMultiStyleConfig
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {colors, styleConfigChakraKeys} from '../../constants'
import {SoundOffIcon, SoundOnIcon} from '../icons'
import ReactPlayer from 'react-player'

const positions = {
    leftBottom: {
        left: '0',
        bottom: '0'
    },
    leftTop: {
        left: '0',
        top: '0'
    },
    rightBottom: {
        right: '0',
        bottom: '0'
    },
    rightTop: {
        right: '0',
        top: '0'
    }
}

const VideoSection = ({
    videoSectionData,
    borderRadius,
    media,
    controls = true,
    ...otherProps
}) => {
    const styles = useMultiStyleConfig(styleConfigChakraKeys.videoSection)
    const [muted, setMuted] = React.useState(true)
    const [playing, setPlaying] = React.useState(true)
    const [isTab] = useMediaQuery('(min-width: 600px)')

    const title = videoSectionData?.text
    const position = positions[videoSectionData?.textInAsset ?? 'leftBottom']
    const mediaUrl = videoSectionData?.media?.desktop?.url

    const isTitleCentered = videoSectionData?.textInAsset === 'center'

    return !(videoSectionData || media) ? (
        <Flex alignItems="center" justifyContent="center">
            <Skeleton width="90%" height={{base: '300px', lg: '650px'}} />
        </Flex>
    ) : (
        <Box
            {...styles.container}
            paddingX={{base: '16px', md: '60px', xl: '134px'}}
            {...otherProps}
            position="relative"
        >
            <Box {...styles.videoContainer} borderRadius={borderRadius}>
                {!isTitleCentered ? (
                    <Box
                        position="absolute"
                        {...position}
                        {...styles.content}
                        zIndex={1000}
                    >
                        {controls && (
                            <Box {...styles.audioIcon}>
                                {muted ? (
                                    <SoundOffIcon
                                        onClick={() => setMuted(false)}
                                        cursor="pointer"
                                        width="100%"
                                        height="100%"
                                        size={60}
                                        zIndex={1000}
                                    />
                                ) : (
                                    <SoundOnIcon
                                        onClick={() => setMuted(true)}
                                        cursor="pointer"
                                        width="100%"
                                        height="100%"
                                        size={60}
                                    />
                                )}
                            </Box>
                        )}
                        {title && <Box {...styles.videoTitle}>{title}</Box>}
                    </Box>
                ) : (
                    <Box
                        position="absolute"
                        zIndex="1001"
                        alignItems="end"
                        display="flex"
                        justifyContent="center"
                        height="100%"
                        width="100%"
                        pb={{base: '50px', md: '50px', lg: '92px'}}
                    >
                        <Flex
                            flexDirection="column"
                            gap={{base: '10px', lg: '25px'}}
                            color={colors.white}
                        >
                            {title && (
                                <Text
                                    fontSize={{base: '32px', lg: '64px'}}
                                    fontWeight="300"
                                    fontFamily="Raleway"
                                    lineHeight="40px"
                                    align="center"
                                >
                                    {title}
                                </Text>
                            )}
                            {videoSectionData && (
                                <Text
                                    align="center"
                                    fontSize={{base: '16px', lg: '25px'}}
                                    fontWeight="300"
                                    fontFamily="Roboto"
                                    lineHeight="26px"
                                >
                                    {videoSectionData?.subText}
                                </Text>
                            )}
                        </Flex>
                    </Box>
                )}

                <Box
                    onClick={() => controls && setPlaying(!playing)}
                    onKeyDown={() => {}}
                    role="button"
                    tabIndex={0}
                    sx={{
                        div: {
                            video: {
                                objectFit: 'cover'
                            }
                        }
                    }}
                >
                    <ReactPlayer
                        url={videoSectionData ? mediaUrl : media}
                        loop
                        muted={muted}
                        playing={playing}
                        width="100%"
                        height="100%"
                        style={{
                            objectFit: 'cover',
                            aspectRatio: !isTab ? '3/4' : '16/9'
                        }}
                        playsinline
                    />
                </Box>
            </Box>
        </Box>
    )
}

VideoSection.displayName = 'VideoSection'

VideoSection.propTypes = {
    videoSectionData: PropTypes.shape({
        textAssetCollection: PropTypes.shape({
            items: PropTypes.arrayOf(
                PropTypes.shape({
                    text: PropTypes.string,
                    subText: PropTypes.string,
                    media: PropTypes.shape({
                        desktop: PropTypes.shape({
                            url: PropTypes.string
                        })
                    }),
                    textInAsset: PropTypes.oneOf([
                        'leftTop',
                        'leftBottom',
                        'rightTop',
                        'rightBottom'
                    ])
                })
            )
        })
    }),
    isTitleCentered: PropTypes.bool
}

export default VideoSection
